import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { AvatarComponent } from '../../molecules/avatar/avatar.component';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { TranslatePipe } from '@ngx-translate/core';
import { MemberOption } from '../member-multiselect-input/member-multiselect-input.interfaces';
import { MemberSelectPanelComponent } from '../member-select-panel/member-select-panel.component';
import {
  DropdownCoordinationService,
  handleTriggerKeydown,
} from '../dropdown-coordination.service';
import { MemberBadgeGroupStore } from './member-badge-group.store';
import { MemberBadgeItem } from './member-badge-group.interfaces';

export type { MemberBadgeItem } from './member-badge-group.interfaces';

@Component({
  selector: 'ocp-member-badge-group',
  imports: [AvatarComponent, HlmPopoverImports, MemberSelectPanelComponent, TranslatePipe],
  templateUrl: './member-badge-group.component.html',
  styleUrl: './member-badge-group.component.scss',
  providers: [MemberBadgeGroupStore],
  host: { class: 'member-badge-group' },
})
export class MemberBadgeGroupComponent {
  private readonly dropdownCoordination = inject(DropdownCoordinationService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(MemberBadgeGroupStore);

  readonly members = input<MemberBadgeItem[]>([]);
  readonly maxVisible = input(3);
  readonly options = input<MemberOption[]>([]);
  readonly selectedIds = input<string[]>([]);

  readonly membersChange = output<string[]>();

  private readonly panel = viewChild<MemberSelectPanelComponent>('panel');

  protected readonly selectionOverlayState = signal<'open' | 'closed' | null>(null);
  protected readonly tooltipVisible = signal(false);
  protected readonly tooltipPosition = signal<{ top: string; left: string } | null>(null);

  private tooltipHideTimeout: ReturnType<typeof setTimeout> | null = null;
  private tooltipShowTimeout: ReturnType<typeof setTimeout> | null = null;
  private scrollListener: (() => void) | null = null;
  private resizeListener: (() => void) | null = null;

  constructor() {
    effect(() => this.store.members.set(this.members()));
    effect(() => this.store.maxVisible.set(this.maxVisible()));
    effect(() => this.store.options.set(this.options()));
    effect(() => this.store.localSelectedIds.set(this.selectedIds()));

    this.dropdownCoordination.registerCloseHandler(
      this.elementRef,
      () => this.closePopover(),
      this.destroyRef
    );
  }

  protected onBadgeClick(): void {
    if (!this.store.selectable()) return;
    this.cancelShowTooltip();
    this.cancelHideTooltip();
    this.tooltipVisible.set(false);
    this.dropdownCoordination.notifyOpen(this.elementRef);
    this.selectionOverlayState.set('open');
  }

  protected onBadgeKeydown(event: KeyboardEvent): void {
    if (!this.store.selectable()) return;
    if (handleTriggerKeydown(event)) {
      this.onBadgeClick();
    }
  }

  protected onSelectionStateChanged(state: 'open' | 'closed'): void {
    if (state === 'closed') this.selectionOverlayState.set(null);
    this.store.isActive.set(state === 'open');
    if (state === 'open') {
      this.panel()?.reset();
      this.panel()?.focusSearch();
    } else {
      this.panel()?.reset();
    }
  }

  protected onMemberToggled(memberId: string): void {
    const updated = this.store.toggleMember(memberId);
    this.membersChange.emit(updated);
    this.closePopover();
  }

  protected showTooltipPopover(): void {
    if (this.store.isActive() || this.store.members().length === 0) return;
    this.cancelHideTooltip();
    this.tooltipShowTimeout = setTimeout(() => {
      this.calculateTooltipPosition();
      this.tooltipVisible.set(true);
      this.attachTooltipListeners();
    }, 300);
  }

  protected scheduleHideTooltip(): void {
    this.cancelShowTooltip();
    this.tooltipHideTimeout = setTimeout(() => {
      this.tooltipVisible.set(false);
      this.detachTooltipListeners();
    }, 150);
  }

  private attachTooltipListeners(): void {
    if (!this.scrollListener) {
      this.scrollListener = () => this.calculateTooltipPosition();
      window.addEventListener('scroll', this.scrollListener, true);
    }
    if (!this.resizeListener) {
      this.resizeListener = () => this.calculateTooltipPosition();
      window.addEventListener('resize', this.resizeListener);
    }
  }

  private detachTooltipListeners(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
  }

  private calculateTooltipPosition(): void {
    const hostEl = this.elementRef.nativeElement;
    const rect = hostEl.getBoundingClientRect();
    const gap = 8;
    const tooltipMaxWidth = 200; // matches max-width in SCSS

    // Clamp left so the tooltip doesn't overflow the right or left viewport edge
    const left = Math.max(0, Math.min(rect.left, window.innerWidth - tooltipMaxWidth - gap));
    this.tooltipPosition.set({
      top: `${rect.top - gap}px`,
      left: `${left}px`,
    });
  }

  protected cancelHideTooltip(): void {
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }
  }

  private cancelShowTooltip(): void {
    if (this.tooltipShowTimeout) {
      clearTimeout(this.tooltipShowTimeout);
      this.tooltipShowTimeout = null;
    }
  }

  protected closePopover(): void {
    this.selectionOverlayState.set('closed');
  }
}
